import axios from "axios";
import React, { createContext, useCallback, useContext, useState } from "react";
import { AuthContext } from "../../AuthContext";
import { useLoading } from "../../LoadingContext";
import { CursorResponse } from "../../../types/queryParams";

export interface IActivity {
  _id: string;
  admin: {
    _id: string;
    username: string;
  };
  ip: string;
  path: string;
  objectBefore: Record<string, any>;
  objectAfter: Record<string, any>;
  metadataEntity: string;
  metadataAction: string;
  roleId: {
    _id: string;
    roleName: string;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface IActivityCollection<D> {
  initialized: boolean;
  data: D;
}

interface IPagination<D> {
  totalCount: number;
  activities: D[];
}

type newCursorResponse<T> = CursorResponse<T> & IPagination<T>;

interface IActivityContext {
  collection: {
    entities: IActivityCollection<{ entities: string[] }>;
    actions: IActivityCollection<{ actions: string[] }>;
    list: IActivityCollection<newCursorResponse<IActivity>>;
  };
  getListv2: (qs: string) => Promise<void>;

  getEnitityActivity: () => Promise<void>;
  getActionActivity: () => Promise<void>;
}

export const ActivityLogsServiceContext =
  createContext<IActivityContext | null>(null);

export default function ActivityLogsService({
  children,
}: {
  children: React.ReactNode;
}) {
  const [collectionList, setCollectionList] = useState<
    IActivityContext["collection"]["list"]
  >({
    initialized: false,
    data: {
      totalCount: 0,
      activities: [],
      beforeCursor: undefined,
      afterCursor: undefined,
      hasAfter: false,
      hasBefore: false,
    },
  });
  const [collectionEntities, setCollectionEntities] = useState<
    IActivityContext["collection"]["entities"]
  >({
    initialized: false,
    data: {
      entities: [],
    },
  });
  const [collectionAction, setCollectionAction] = useState<
    IActivityContext["collection"]["actions"]
  >({
    initialized: false,
    data: {
      actions: [],
    },
  });

  const authContext = useContext(AuthContext);
  const loading = useLoading();

  const onFetch = useCallback(
    async (url: string, body: Record<string, string | number>) => {
      loading.push();
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_API_URL}${url}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${authContext.token}`,
            },
            validateStatus: () => true,
          },
        );

        return resp.data;
      } catch (e) {
        return {};
      } finally {
        loading.pop();
      }
    },
    [authContext],
  );
  const onFetchGet = useCallback(
    async (url: string, params: Record<string, string | number>) => {
      loading.push();
      try {
        const resp = await axios.get(`${process.env.REACT_APP_API_URL}${url}`, {
          params,
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
          validateStatus: () => true,
        });

        return resp.data;
      } catch (e) {
        return {};
      } finally {
        loading.pop();
      }
    },
    [authContext],
  );

  //make getListv2 a function that takes a payload and returns a promise
  async function getListv2(qs: string) {
    const { data } = await onFetchGet(`/api/v2/activity/list?${qs}`, {});
    if (data) {
      setCollectionList({
        initialized: true,
        data: {
          ...data,
          activities: data.records,
        },
      });
    }
  }
  async function getEnitityActivity() {
    const { data } = await onFetch("/api/v1/activity/list-entities", {});
    if (data) {
      setCollectionEntities({
        initialized: true,
        data,
      });
    }
  }

  async function getActionActivity() {
    const { data } = await onFetch("/api/v1/activity/list-actions", {});
    if (data) {
      setCollectionAction({
        initialized: true,
        data,
      });
    }
  }

  return (
    <ActivityLogsServiceContext.Provider
      value={{
        collection: {
          list: collectionList,
          entities: collectionEntities,
          actions: collectionAction,
        },
        getListv2,
        getEnitityActivity,
        getActionActivity,
      }}
    >
      {children}
    </ActivityLogsServiceContext.Provider>
  );
}
